import { Breadcrumbs, Modal, useSnackBar } from "@truevault/aquilo";
import _ from "lodash";
import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Redirect } from "react-router";
import { useSurvey } from "../../../../common/hooks/api/useSurvey";
import { BiometricIdentifiersPolicyEditor } from "../../../components/privacyCenter/BiometricIdentifiersPolicyEditor";
import {
  ProgressFooterSaveNotification,
  useEditPrivacyCenter,
  useUpdateNoticeApi,
} from "../../../components/privacyCenter/EditPrivacyCenter";
import { StandardPageWrapper } from "../../../components/layout/StandardPageWrapper";
import { PageLoading } from "../../../components";
import { usePrivacyCenterNotice } from "../../../hooks/usePrivacyCenterNotice";
import { Routes } from "../../../root/routes";
import {
  COLORADO_BIOMETRIC_IDENTIFIERS_QUESTION_SLUG,
  COLORADO_EMPLOYEES_QUESTION_SLUG,
  HR_SURVEY_NAME,
} from "../../../surveys/hrSurvey";
import { PrivacyCenterNoticeDto } from "../../../../common/service/server/dto/PrivacyCenterDto";
import { Api } from "../../../../common/service/Api";
import { PrintPreviewPublishButtons } from "../privacy-center/us-state-notices/USStateNoticesPage";

const COLORADO_EMPLOYEE_BIOMETRIC_SECTION_ANCHOR = "colorado-employee-biometric-identifiers-policy";

export function ColoradoEmployeeBiometricNoticePage() {
  const editPrivacyCenter = useEditPrivacyCenter();
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [hasNoticeContent, setHasNoticeContent] = useState(false);
  const editorRef = useRef<ColoradoEmployeeBiometricNoticeEditorHandle>(null);
  const organizationId = editPrivacyCenter?.props?.org?.id;
  const privacyCenterId = editPrivacyCenter?.props?.privacyCenter?.id;
  const draftNoticeId = editPrivacyCenter?.props?.draftPrivacyPolicyNoticeId;
  const [draftNotice, draftNoticeRequest, refreshDraftNotice] = usePrivacyCenterNotice(
    organizationId,
    privacyCenterId,
    draftNoticeId,
  );
  const { answers: hrAnswers, request: hrSurveyRequest } = useSurvey(
    organizationId,
    HR_SURVEY_NAME,
  );
  const section = draftNotice?.sections.find(
    (candidate) => candidate.anchor === COLORADO_EMPLOYEE_BIOMETRIC_SECTION_ANCHOR,
  );
  const { updateNotice } = useUpdateNoticeApi();
  const enqueueSnackbar = useSnackBar();

  useEffect(() => {
    setHasNoticeContent(Boolean(section?.content.trim()));
  }, [section?.content]);

  const sendPublish = async () => {
    setIsPublishing(true);
    try {
      await editorRef.current?.flushPendingSave();
      const latestPublishedNoticeDate = editPrivacyCenter?.props?.latestPublishedNoticeUpdatedOn;
      if (latestPublishedNoticeDate) {
        await updateNotice({
          organizationId,
          privacyCenterId,
          noticeId: draftNotice.publicId,
          updateNoticeBody: {
            publishedAt: latestPublishedNoticeDate,
          },
        });
      }
      const publishedNotice = await updateNotice({
        organizationId,
        privacyCenterId,
        noticeId: draftNotice.publicId,
        updateNoticeBody: {
          status: "PUBLISHED",
        },
      });
      try {
        await Api.notice.archiveColoradoEmployeeBiometricNoticePdf(
          organizationId,
          privacyCenterId,
          publishedNotice.publicId,
        );
      } catch (error) {
        console.error("Colorado HR biometric notice published, but PDF archiving failed:", error);
      }

      await Promise.all([
        refreshDraftNotice(),
        editPrivacyCenter?.props?.privacyCenterRefresh(),
        editPrivacyCenter?.props?.allNoticesRefresh(),
      ]);
      enqueueSnackbar("Colorado HR biometric notice published.", { variant: "success" });
    } catch (error) {
      console.error("Failed to publish the Colorado HR biometric notice:", error);
      enqueueSnackbar("Failed to publish. Please try again.", { variant: "error" });
    } finally {
      setIsPublishing(false);
    }
  };

  if (!draftNoticeRequest.success || !hrSurveyRequest.success || !draftNotice) {
    return <PageLoading />;
  }

  const qualifies =
    hrAnswers?.[COLORADO_EMPLOYEES_QUESTION_SLUG] === "true" &&
    hrAnswers?.[COLORADO_BIOMETRIC_IDENTIFIERS_QUESTION_SLUG] === "true";

  if (!qualifies || !section) {
    return <Redirect to={Routes.hrCenter.root} />;
  }

  return (
    <StandardPageWrapper fullWidth>
      <div className="tw-boundary mb-12">
        <Breadcrumbs
          source="HR Notices"
          title="Colorado Biometric Identifiers Notice for Employees and Contractors"
          sourceRoute={Routes.hrCenter.hrNotices}
        />
      </div>
      <ColoradoEmployeeBiometricNoticeEditor
        ref={editorRef}
        organizationId={organizationId}
        privacyCenterId={privacyCenterId}
        draftNotice={draftNotice}
        sectionId={section.id}
        sectionHeader={section.header}
        initialContent={section.content}
        placeholder={
          section.placeholder ||
          "Enter your Colorado biometric identifiers notice for employees and contractors..."
        }
        refreshDraftNotice={refreshDraftNotice}
        setIsSaving={setIsSaving}
        setIsSaved={setIsSaved}
        onContentChange={(newContent) => setHasNoticeContent(Boolean(newContent.trim()))}
      />
      <div style={{ maxWidth: "764px" }}>
        <div className="tw-boundary">
          <div className="mt-4 flex items-center justify-end gap-4">
            <div className="flex items-center whitespace-nowrap text-neutral-600 font-normal">
              <ProgressFooterSaveNotification saving={isSaving} isSaved={isSaved} />
            </div>
            <PrintPreviewPublishButtons
              orgId={organizationId}
              privacyCenter={editPrivacyCenter?.props?.privacyCenter}
              hasPublishedNotice={editPrivacyCenter?.props?.latestPublishedNoticeUpdatedOn != null}
              onPublishClick={() => setShowPublishModal(true)}
              showPdfPreviewButton={false}
              isPublishEnabled={hasNoticeContent && !isSaving && !isPublishing}
              isPublishing={isPublishing}
              publishDisabledTooltip={
                hasNoticeContent
                  ? "Wait for your changes to finish saving."
                  : "Add notice content before publishing."
              }
            />
          </div>
        </div>
      </div>
      {showPublishModal && (
        <Modal
          showOverlay
          firstButton="Go Back"
          firstButtonColor="tertiary"
          onFirstButtonClick={() => setShowPublishModal(false)}
          secondButton="Publish"
          secondButtonColor="primary"
          secondButtonDisabled={isPublishing}
          onSecondButtonClick={() => {
            setShowPublishModal(false);
            sendPublish();
          }}
          closeButton
          onClose={() => setShowPublishModal(false)}
          alignButton="right"
          alignText="left"
          title="You are about to publish your Colorado HR biometric notice"
        />
      )}
    </StandardPageWrapper>
  );
}

type ColoradoEmployeeBiometricNoticeEditorHandle = {
  flushPendingSave: () => Promise<void>;
};

type ColoradoEmployeeBiometricNoticeEditorProps = {
  organizationId: string;
  privacyCenterId: string;
  draftNotice: PrivacyCenterNoticeDto;
  sectionId: string;
  sectionHeader: string;
  initialContent: string;
  placeholder: string;
  refreshDraftNotice: () => Promise<void>;
  setIsSaving: React.Dispatch<React.SetStateAction<boolean>>;
  setIsSaved: React.Dispatch<React.SetStateAction<boolean>>;
  onContentChange: (content: string) => void;
};

const ColoradoEmployeeBiometricNoticeEditor = React.forwardRef<
  ColoradoEmployeeBiometricNoticeEditorHandle,
  ColoradoEmployeeBiometricNoticeEditorProps
>(function ColoradoEmployeeBiometricNoticeEditor(
  {
    organizationId,
    privacyCenterId,
    draftNotice,
    sectionId,
    sectionHeader,
    initialContent,
    placeholder,
    refreshDraftNotice,
    setIsSaving,
    setIsSaved,
    onContentChange,
  },
  ref,
) {
  const [content, setContent] = useState(initialContent);
  const savedStatusTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { updateNotice } = useUpdateNoticeApi();

  const debouncedSave = useCallback(
    _.debounce(async (newContent: string) => {
      setIsSaving(true);
      setIsSaved(false);
      try {
        await updateNotice({
          organizationId,
          privacyCenterId,
          noticeId: draftNotice.publicId,
          updateNoticeBody: {
            sections: [{ id: sectionId, content: newContent }],
          },
        });
        await refreshDraftNotice();
        setIsSaved(true);
        if (savedStatusTimeout.current) {
          clearTimeout(savedStatusTimeout.current);
        }
        savedStatusTimeout.current = setTimeout(() => setIsSaved(false), 2000);
      } finally {
        setIsSaving(false);
      }
    }, 750),
    [],
  );

  useImperativeHandle(
    ref,
    () => ({
      flushPendingSave: async () => {
        await debouncedSave.flush();
      },
    }),
    [debouncedSave],
  );

  useEffect(
    () => () => {
      debouncedSave.cancel();
      if (savedStatusTimeout.current) {
        clearTimeout(savedStatusTimeout.current);
      }
    },
    [debouncedSave],
  );

  return (
    <BiometricIdentifiersPolicyEditor
      audience="employee"
      header={sectionHeader}
      content={content}
      placeholder={placeholder}
      onContentChange={(newContent) => {
        setContent(newContent);
        onContentChange(newContent);
        debouncedSave(newContent);
      }}
    />
  );
});
